<template>
<div data-component="LocationInput.vue" class="form-input-group form-input-location">
	<div class="relative | !shadow-none">
		<div class="absolute w-full">
			<Loader v-if="ShowGeolocate && ShowLoader"/>
		</div>
		<label class="sr-only" for="ModalLocationZipInput">
			Kod pocztowy
		</label>
		<Autocomplete
			v-if="DisplayZipInput"
			ref="autocompleteZip"
			class="form-location-autocomplete-zip | !static mr-3 basis-32 [&>input]:px-0 [&>input]:text-center"
			:modelValue="ReverseCity"
			:Autocomplete="SearchUrl"
			:Placeholder="'00-000'"
			InputName="ModalLocationZipInput"
			:DataTest="`${DataTest}__zip`"
			@process="OnProcess"
			@select="OnSelect"
			@blur="OnBlur($event, 'zip')"
			@keydown="OnZipCode($event, true)"
		/>
		<label class="sr-only" for="ModalLocationInput">
			{{ DisplayZipInput ? 'Miejscowość' : 'Miejscowość lub kod pocztowy' }}
		</label>
		<Autocomplete
			ref="autocomplete"
			class="form-location-autocomplete-location | !static"
			InputName="ModalLocationInput"
			:modelValue="Location.City"
			:Autocomplete="SearchUrl"
			:Placeholder="DisplayZipInput ? 'Miejscowość' : 'Miejscowość lub kod pocztowy'"
			:Autoselect="!(InFormGroup && DisplayZipInput)"
			:DataTest="`${DataTest}__select`"
			:htmlAttributeAutocomplete="htmlAttributeAutocomplete"
			:isValidated="inputCityValidationMessage !== ''"
			:focus="ShouldBeFocused"
			@process="OnProcess"
			@select="OnSelect"
			@blur="OnBlur($event, 'city')"
		/>
		<div v-if="ShowCountry" class="!absolute right-1 flex mr-3" :data-test="`${DataTest}__arrow`">
			<button v-if="ShowGeolocate" class="geolocate-icon | text-neutral-600 hover:text-accent mr-3" title="Pobierz moją lokalizację" @click="Geolocate('ask')">
				<OIcon name="crosshair" size="1.5"/>
			</button>
			<DropdownSelect
				:modelValue="Location.Country[0]"
				:Items="locationStore.countries"
				@change="OnCountryChange"
			/>
		</div>
	</div>

	<div
		v-if="inputCityValidationMessage"
		class="flex items-center mt-1 text-danger"
	>
		<OIcon name="warning-in-circle" class="mr-1 "/>
		<span class="text-xs blox">{{ inputCityValidationMessage }}</span>
	</div>

	<div v-if="((Location.City[1] || (Location.City[0] && !Location.City[1])) && !Location.IsRecognized) || !IsAutocomplete" class="mt-5 location-input-details">
		<div class="form-group is-editable" :class="{ 'is-multicolumn': ScreenSize > 0 }">
			<div
				v-if="IsAutocomplete && !Location.IsRecognized && (!Location.District[0]) && Location.Country[0] === 'locationPl' && !OnlyCity"
				class="[ form-input-validation ] text-danger text-xs -mt-3"
			>
				<div v-if="!Location.City[0]" v-text="'Wprowadź poprawny kod pocztowy'"/>
				<div v-else-if="!Location.City[1]" v-text="'Wprowadź poprawną nazwę miejscowości'"/>
				<div v-else v-text="'Nie znaleźliśmy takiej miejscowości. Popraw wpisaną nazwę lub podaj dokładne dane miejscowości: województwo i powiat.'"/>
			</div>
			<template v-if="Location.Country[0] === 'locationPl'">
				<div v-if="ShowRegion" class="mt-2 form-input col-half" :data-invalid="Boolean(Validation.Region[0])">
					<label class="flex items-center form-input-label">Województwo</label>
					<DropdownSelect v-model="Location.Region[0]" :Items="locationStore.poland" placeholder="Wybierz województwo" class="input" @change="OnRegionChange"/>
					<div v-if="Validation.Region[0]" class="[ form-input-validation ] text-danger text-xs mt-2" v-html="Validation.Region[1]"/>
				</div>
				<div v-if="Location.Region[0] && ShowDistrict" class="mt-2 form-input col-half" :data-invalid="Boolean(Validation.District[0])">
					<label class="flex items-center form-input-label">Powiat</label>
					<DropdownSelect v-model="Location.District[0]" :Items="DistrictList" placeholder="Wybierz powiat" class="input" @change="OnDistrictChange"/>
					<div v-if="Validation.District[0]" class="[ form-input-validation ] text-danger text-xs mt-2" v-html="Validation.District[1]"/>
				</div>
				<div v-if="(Location.District[0] && Location.Region[0] && ShowZip && !DisplayZipInput) || Location.IsTransport" class="mt-2 form-input col-half" :data-invalid="Boolean(Validation.Zip[0])">
					<label class="flex items-center form-input-label">Kod pocztowy</label>
					<input v-model="Location.City[0]" type="text" maxlength="12" @keydown="OnZipCode($event, true)" @keyup="Validation.Zip[0] = false">
					<div v-if="Validation.Zip[0]" class="[ form-input-validation ] text-danger text-xs mt-2" v-html="Validation.Zip[1]"/>
				</div>
			</template>
			<template v-if="Location.Country[0] !== 'locationPl'">
				<div class="mt-2 form-input col-half" :data-invalid="Boolean(Validation.Zip[0])">
					<label class="flex items-center form-input-label">Kod pocztowy</label>
					<input
						v-model="Location.City[0]"
						type="text"
						:class="{ '!border !border-danger': Validation.Zip[0] }"
						@blur="ValidateLocation"
					>
					<div v-if="Validation.Zip[0]" class="[ form-input-validation ] text-xs flex items-center mt-1 text-danger">
						<OIcon name="warning-in-circle" class="mr-1 "/>
						{{ Validation.Zip[1] }}
					</div>
				</div>
			</template>
		</div>
	</div>
</div>
</template>

<script lang="ts">
import { mapStores } from 'pinia';
import { useLocationStore } from '@globals/stores/location';
import Geolocation from '@globals/utilities/Geolocation';
/**
	 * Komponent odpowiedzialny za kontrolkę wyboru lokalizacji.
	 */
export default {
	name: 'LocationInput',
	props: {
		modelValue: { type: Object, default: {} }, // eslint-disable-line
		OnlyZip: { type: Boolean, default: false },

		/**
			 * Pokazuj kod pocztowy, a także pozwól na wyszukanie lokalizacji
			 * poprzez wpisanie kodu.
			 */
		ShowZip: { type: Boolean, default: true },
		ShowRegion: { type: Boolean, default: true },
		ShowDistrict: { type: Boolean, default: true },
		ShowGeolocate: { type: Boolean, default: false },
		ShowCountry: { type: Boolean, default: true },
		LocationZip: { type: String, default: '' },
		// czy chcemy podać tylko miasto czy część z wojewódeztwem itp
		OnlyCity: { type: Boolean, default: false },
		InFormGroup: { type: Boolean, default: false },
		DataTest: { type: String, default: '' },
		htmlAttributeAutocomplete: { default: 'off', type: String },
		inputCityValidationMessage: { type: String, default: '' },
		Form: { type: Object },
		ChosenDistrict: { type: String, default: '' },
	},
	emits: ['change', 'update:modelValue', 'clear-input-city-validation-message'],
	data() {
		return {
			Location: {},
			Validation: {
				Input: [false, 'Wpisz poprawną nazwę miejscowości'],
				Region: [false, 'Wybierz województwo z listy'],
				District: [false, 'Wybierz powiat z listy'],
				Zip: [false, 'Wpisz poprawny kod pocztowy'],
			},
			DistrictList: {},
			ShowLoader: false,
			WasEdited: false,
			ShouldBeFocused: false,
		};
	},
	computed: {
		...mapStores(useLocationStore),
		SearchUrl() {
			if (!this.IsAutocomplete) { return ''; }
			if (this.Location.Country && this.Location.Country[0] === 'locationPl' && this.DisplayZip) { return 'Location'; }

			return `/common/dictionary/zip/${this.DisplayZip ? 'search-by-zip' : 'search'}.ofer?query={{q}}&cc=${this.Location.Country[0] === 'locationPl' ? 'PL' : 'DE'}`;
		},
		IsAutocomplete() {
			return this.Location.Country && (this.Location.Country[0] === 'locationPl' || this.Location.Country[0] === 'locationDe');
		},
		Validate() {
			return 'x';
		},
		DisplayZip() {
			return this.Location.DisplayZip && this.IsAutocomplete;
		},
		ReverseCity() {
			return this.Location.City.slice().reverse();
		},

		/**
			 * Czy pokazać pole do wpisywania kodu pocztowego?
			 * Dzieje się to tylko kiedy pokazujemy kod, wybrana została
			 * Polska, a lokalizacja nie jest nierozpoznana.
			 */
		DisplayZipInput() {
			const loc = this.Location;
			return this.DisplayZip && (loc.Country[0] === 'locationPl'); //  || loc.Country[0] === 'locationDe' && !(loc.City[1] && !loc.IsRecognized)
		},
	},
	watch: {
		modelValue(n, o) {
			this.Location = JSON.parse(JSON.stringify(n));
		},
	},
	created() {
		if (this.LocationZip) {
			this.FetchLocationFromZip(this.LocationZip);
		}
		this.Location = JSON.parse(JSON.stringify(this.modelValue));
		if (!this.Location.Country[0]) { this.Location.Country = ['locationPl', 'Polska']; }
		if (!this.Location.City[0]) {
			if (Array.isArray(this.Location.District[0])) { this.Location.District[0] = ''; this.Location.IsRecognized = false; }
			if (Array.isArray(this.Location.Region[0])) { this.Location.Region[0] = ''; this.Location.IsRecognized = false; }
		}
		if (!this.LocationZip && this.Location.City[1] === '') { this.ShouldBeFocused = true; }
	},
	methods: {
		async Geolocate(mode) {
			this.ShowLofer = true;
			const geo = await Geolocation.Geolocate(mode);
			if (geo) {
				await fetch(`/common/dictionary/zip/search.ofer?query=${geo.zip}&cc=PL`)
					.then((res) => res.json())
					.then((res) => {
						this.OnSelect(res[0]);
						this.ShowLoader = false;
					});
			} else {
				this.ShowLoader = false;
			}
		},

		async FetchLocationFromZip(zip: string) {
			if (zip && !this.modelValue.City[0]) {
				try {
					this.ShowLoader = true;
					let response = await fetch(`/common/dictionary/zip/search.ofer?query=${zip}&cc=PL`);
					response = await response.json();
					if (this.Form && this.Form.EntryOptions.City && this.Form.EntryOptions.Zip) {
						const element = response.find((location) => location.city === this.Form.EntryOptions.City);
						this.OnSelect(element);
					} else {
						this.OnSelect(response[0]);
					}
				} catch (err) {
					console.error(err);
				} finally {
					this.ShowLoader = false;
				}
			}
		},

		OnProcess(rsvp, widget) {
			if (!this.IsAutocomplete) {
				widget.WritableSuggestions = [];
				return;
			}
			this.ClearLocation(false);

			if (rsvp.length) {
				// Mapa do zliczania wystąpień każdej kombinacji city-idLocation
				const cityLocationCount = {};
				rsvp.forEach((l) => {
					// Tworzenie klucza dla kombinacji city-idLocation
					const key = `${l.city}-${l.idLocation}`;
					if (cityLocationCount[key]) {
						cityLocationCount[key]++;
					} else {
						cityLocationCount[key] = 1;
					}
				});

				rsvp.forEach((l) => {
					l.Label = '';
					if (this.DisplayZip) {
						l.Label += `<span class="mr-1 font-bold location-zip text-neutral-600">${l.zip}</span>`;
					}

					l.Label += `<span class="location-city">${l.city}</span>`;

					// Ponowne tworzenie klucza
					const key = `${l.city}-${l.idLocation}`;
					// Sprawdzenie, czy kombinacja występuje więcej niż raz
					if (l.location) {
						const locationText = cityLocationCount[key] > 1 ? `${l.location}, ${l.district}` : l.location;
						l.Label += `<span class="location-region | text-neutral-600 text-xs">${locationText}</span>`;
					}
					l.Value = l.city;
					l.Input = l.city;
				});

				widget.InputSuggestions = rsvp;
			} else {
				// wyczyść kod pocztowy, jeśli miejsowość nie istnieje
				this.modelValue.City[0] = '';
				widget.InputSuggestions = [];
			}
		},

		/**
			 * Blokuje wprowadzenie znaków nie będących cyfrą bądź myślnikiem.
			 * @param e Obiekt zdarzenia
			 * @param insertDash Czy wstawić separator po drugiej cyfrze?
			 */
		OnZipCode(e:KeyboardEvent, insertDash = false) {
			const isNumber = e.key === String(Number.parseInt(e.key)) || e.key === '-';
			if ((e.keyCode >= 40 && e.keyCode <= 126) && !isNumber) {
				e.preventDefault();
			}

			const target = e.target as HTMLInputElement;
			if (insertDash && target && target.value.length === 2 && Number.isInteger(e.key)) {
				target.value += '-';
			}
		},

		OnSelect(l) {
			const ll = this.Location;
			ll.City = [l.zip || ll.City[0], l.city];
			ll.Region = [l.idLocation, ''];
			ll.District = [l.idDistrict, ''];
			ll.Coords = [l.latitude, l.longitude];
			ll.Zip = l.zip;
			ll.IsRecognized = true;
			ll.Id = l.id;
			ll.CityDistrictList = l.cityDistrictList || [];
			this.CurrentState.LocationZip = ll.Zip;
			this.OnInputChange();
			this.$emit('change', this.Location);
		},
		OnBlur(e, source) {
			if (e && source) {
				// Ustalamy, czy input został zmodyfikowany, aby w razie czego go walidować.
				this.WasEdited = true;

				if (source === 'city') {
					this.Location.City = [e[0] || this.Location.City[0], e[1]];
				}
				if (source === 'zip') {
					this.Location.City = [e[1], e[0]];
				}
			}
			window.setTimeout(() => {
				this.OnInputChange();
				this.Location.IsRecognized = Boolean(!(!this.Location.Region[0] || !this.Location.District[0]) && (Boolean(this.Location.City[0]) && Boolean(this.Location.City[1])));
				if (source === 'zip' && (!e[0] && e[1] || !e)) {
					this.Location.IsRecognized = false;
				}
			}, 100);
		},
		OnCountryChange(e) {
			this.ClearLocation(true);
			const c = this.locationStore.countries.find((country) => country.Value === e);
			this.Location.Country = [e, c ? c.Label : ''];
			this.Location.City = ['', ''];
			this.OnInputChange();
		},
		OnInputChange() {
			if (this.Location.Country[0] !== 'locationPl') {
				this.Location.District = this.Location.Region = ['', '']; // FocusEvent, IsTrusted bug!
			}

			const isValid = () => {
				for (const key in this.Validation) {
					if (this.Validation[key][0] === true) {
						return true;
					}
				}
				return false;
			};

			if (!isValid()) {
				this.$emit('clear-input-city-validation-message');
			}
			
			if (this.ChosenDistrict !== '') {
				this.Location.ChosenDistrict = this.ChosenDistrict;
			}
			
			this.$emit('update:modelValue', this.Location);
		},
		ClearLocation(all) {
			this.ClearValidation();

			if (this.$refs.autocomplete) {
				this.$refs.autocomplete.InputSuggestions = [];
			}
			if (this.$refs.autocompleteZip) {
				this.$refs.autocompleteZip.InputSuggestions = [];
			}

			if (all) {
				this.Location.Country = ['locationPl', 'Polska'];
				this.Location.City = ['', ''];
			}
			this.Location.Region = ['', ''];
			this.Location.District = ['', ''];
			this.Location.Coords = ['', ''];
			this.Location.IsRecognized = true;
			this.Location.Id = -1;
		},
		ClearValidation() {
			for (const vv in this.Validation) { this.Validation[vv] = [false, this.Validation[vv][1]]; }
		},
		ValidateLocation(district) {
			this.ClearValidation();
			this.OnBlur();

			if (district) {
				this.Location.District = [district, ''];
			}

			if (!this.Location.City[1]) {
				const validationMessage = this.Validation.Input[1];
				this.Validation.Input = [true, validationMessage];
				return validationMessage;
			}

			const checkNull = (x) => JSON.stringify(x).includes('null');

			const isZipMissing = () => !this.Location.City[0] || checkNull(this.Location.City[0]);
			const isFieldMissing = (field): boolean => !this.Location[field][0] || checkNull(this.Location[field][0]);

			if ((!this.Location.Country[0] || this.Location.Country[0] === 'locationPl') && !this.OnlyZip) {
				if (isZipMissing() || (this.DisplayZipInput && this.WasEdited)) {
					if (this.OnlyCity) {
						return 'Podany kod nie pasuje do żadnej miejscowości';
					}
					const fields = ['Region', 'District'];
					for (const field of fields) {
						if (isFieldMissing(field)) {
							const validationMessage = this.Validation[field][1];
							this.Validation[field] = [true, validationMessage];
							return validationMessage;
						}
					}
					if (this.ShowZip && isZipMissing()) {
						const validationMessage = this.Validation.Zip[1];
						this.Validation.Zip = [true, validationMessage];
						return validationMessage;
					}
					// przypadek gdy miasto nie znajduje się w bazie (nie ma wspolrzednych geograficznych)
				} else if (this.OnlyCity && !isFieldMissing('City') && this.Location.Coords[0] === '') {
					return 'Podana nazwa miasta nie istnieje.';
				}
			} else if (isFieldMissing('City')) {
				const validationMessage = this.Validation.Zip[1];
				this.Validation.Zip = [true, validationMessage];
				return validationMessage;
			} else if (this.Location.City[0].length > 10) {
				return 'Podany kod pocztowy jest za długi';
			}

			this.OnInputChange();
			return '';
		},
		async OnRegionChange(region) {
			const ll = this.Location;
			this.ClearValidation();
			this.DistrictList = [];
			ll.Region = [region, ''];
			ll.District = ['', ''];

			this.DistrictList = await this.locationStore.getDistrict(ll.Region[0]);

			this.OnInputChange();
			window.setTimeout(() => {
				this.Location.IsRecognized = false;
			}, 100);
		},
		OnDistrictChange() {
			this.ValidateLocation();
			window.setTimeout(() => {
				this.Location.IsRecognized = false;
			}, 100);
		},
	},
};
</script>

<style lang="scss">
	.location-input-details .form-group.is-editable.is-multicolumn { max-width: unset; }
</style>
